/**
 * pixi-particles — Utils.ts
 * Small math / object helpers. Zero external dependencies.
 */

export function Clamp(value: number, min: number, max: number): number
{
    return Math.max(min, Math.min(max, value));
}

export function Wrap(value: number, min: number, max: number): number
{
    var range = max - min;

    return (range === 0) ? min : value - (range * Math.floor((value - min) / range));
}

export function SnapTo(value: number, gap: number, start = 0): number
{
    if (gap === 0) { return value; }

    value -= start;
    value = gap * Math.round(value / gap);

    return start + value;
}

export function Between(min: number, max: number): number
{
    return Math.floor(Math.random() * (max - min + 1) + min);
}

export function FloatBetween(min: number, max: number): number
{
    return Math.random() * (max - min) + min;
}

export function DegToRad(degrees: number): number
{
    return degrees * (Math.PI / 180);
}

export function RadToDeg(radians: number): number
{
    return radians * (180 / Math.PI);
}

export function RotateAround<T extends { x: number; y: number }> (point: T, x: number, y: number, angle: number): T
{
    var c = Math.cos(angle);
    var s = Math.sin(angle);

    var tx = point.x - x;
    var ty = point.y - y;

    point.x = tx * c - ty * s + x;
    point.y = tx * s + ty * c + y;

    return point;
}

/**
 * Fetches a value from an object, honouring dot-notation keys (e.g. 'a.b.c'),
 * falling back to a default when missing.
 */
export function GetFastValue (source: any, key: string, defaultValue?: any): any
{
    if (!source)
    {
        return defaultValue;
    }

    var t = typeof source;

    if (t !== 'object')
    {
        return defaultValue;
    }
    else if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined)
    {
        return source[key];
    }
    else if (key.indexOf('.') !== -1)
    {
        var keys = key.split('.');
        var parent = source;
        var value = defaultValue;

        for (var i = 0; i < keys.length; i++)
        {
            if (parent && Object.prototype.hasOwnProperty.call(parent, keys[i]))
            {
                value = parent[keys[i]];
                parent = parent[keys[i]];
            }
            else
            {
                value = defaultValue;
                break;
            }
        }

        return value;
    }
    else
    {
        return defaultValue;
    }
}

export function HasValue (source: any, key: string): boolean
{
    return (source !== undefined && source !== null && Object.prototype.hasOwnProperty.call(source, key));
}

export function HasAny (source: any, keys: string[]): boolean
{
    for (var i = 0; i < keys.length; i++)
    {
        if (source && Object.prototype.hasOwnProperty.call(source, keys[i]))
        {
            return true;
        }
    }

    return false;
}

export function HasAll (source: any, keys: string[]): boolean
{
    for (var i = 0; i < keys.length; i++)
    {
        if (!source || !Object.prototype.hasOwnProperty.call(source, keys[i]))
        {
            return false;
        }
    }

    return true;
}

/** Shallow-merges `source` over `target`, returning a brand new object. */
export function MergeRight (target: any, source: any): any
{
    var output: any = {};

    for (var key in target)
    {
        output[key] = target[key];
    }

    for (var key2 in source)
    {
        output[key2] = source[key2];
    }

    return output;
}

export function GetRandom<T> (array: T[]): T | undefined
{
    if (array.length === 0)
    {
        return undefined;
    }

    return array[Math.floor(Math.random() * array.length)];
}

export function Remove<T> (array: T[], item: T): T[]
{
    var index = array.indexOf(item);

    if (index !== -1)
    {
        array.splice(index, 1);
    }

    return array;
}

/** Packs 0-255 r/g/b components into a single 0xRRGGBB integer. */
export function GetColor (r: number, g: number, b: number): number
{
    return (Clamp(Math.round(r), 0, 255) << 16) + (Clamp(Math.round(g), 0, 255) << 8) + Clamp(Math.round(b), 0, 255);
}

/** Unpacks a 0xRRGGBB integer into { r, g, b } (0-255) components. */
export function IntegerToRGB (color: number): { r: number; g: number; b: number }
{
    return {
        r: (color >> 16 & 0xFF),
        g: (color >> 8 & 0xFF),
        b: (color & 0xFF)
    };
}

export function StableSort<T> (array: T[], compare: (a: T, b: T) => number): T[]
{
    return array.sort(compare);
}

export function EscapeRegExp (s: string): string
{
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
